/**
 * Plain-English reasons why Agent Review actions (Next, Submit, …) stay disabled.
 */

import { artefactRowLabel } from "./artefactReviewCopy";
import { deriveRag } from "./deriveRag";
import type {
  AgentRun,
  ArtefactRating,
  ArtefactRatings,
  PhaseCriteria,
  PhaseRatings,
  StepRating,
} from "./types";
import type { WizardStep } from "./wizardSteps";

function unansweredCheckCount(criteria: PhaseCriteria, rating: StepRating | ArtefactRating): number {
  let n = 0;
  for (const check of criteria.checks) {
    if (!rating.answers[check.id]) n += 1;
  }
  return n;
}

function missingFailCommentCount(
  criteria: PhaseCriteria,
  rating: StepRating | ArtefactRating,
): number {
  let n = 0;
  for (const check of criteria.checks) {
    if (rating.answers[check.id] === "fail" && !rating.check_comments[check.id]?.trim()) {
      n += 1;
    }
  }
  return n;
}

function ratingNeedsNote(criteria: PhaseCriteria, rating: StepRating | ArtefactRating): boolean {
  if (rating.human_rag === null) return false;
  const derived = deriveRag(criteria, rating.answers);
  const overridden = derived.rag !== null && rating.human_rag !== derived.rag;
  return rating.human_rag === "amber" || rating.human_rag === "red" || overridden;
}

/** Reasons a phase rating panel is incomplete (orchestration / output / one artefact). */
export function phaseRatingIncompleteReasons(
  criteria: PhaseCriteria,
  rating: StepRating | ArtefactRating | undefined,
  options?: { requireApproval?: boolean },
): string[] {
  if (!rating) {
    const reasons = ["Answer every checklist item", "Choose your overall rating"];
    if (options?.requireApproval) reasons.push("Choose whether to approve this document");
    return reasons;
  }

  const reasons: string[] = [];
  const unanswered = unansweredCheckCount(criteria, rating);
  if (unanswered > 0) {
    reasons.push(
      unanswered === 1
        ? "Answer the remaining checklist item"
        : `Answer the remaining checklist items (${unanswered} left)`,
    );
  }

  const missingComments = missingFailCommentCount(criteria, rating);
  if (missingComments > 0) {
    reasons.push(
      missingComments === 1
        ? "Add a comment explaining the failed check"
        : `Add comments explaining the failed checks (${missingComments} left)`,
    );
  }

  if (rating.human_rag === null) {
    reasons.push("Choose your overall rating");
  } else if (ratingNeedsNote(criteria, rating) && !rating.human_note.trim()) {
    const derived = deriveRag(criteria, rating.answers);
    const overridden = derived.rag !== null && rating.human_rag !== derived.rag;
    reasons.push(
      overridden
        ? "Add a note explaining why you changed the suggested rating"
        : "Add a note explaining your amber or red rating",
    );
  }

  if (options?.requireApproval && "human_approval" in rating && rating.human_approval === null) {
    reasons.push("Choose whether to approve this document");
  }

  return reasons;
}

/** Reasons artefact ratings across the run are incomplete. */
export function artefactsIncompleteReasons(run: AgentRun, ratings: ArtefactRatings): string[] {
  const artefacts = run.artefacts ?? [];
  const criteria = run.artefact_review_criteria;
  if (!artefacts.length || !criteria) return [];

  const incomplete: string[] = [];
  for (const artefact of artefacts) {
    const itemReasons = phaseRatingIncompleteReasons(criteria, ratings[artefact.artifact_id], {
      requireApproval: true,
    });
    if (itemReasons.length === 0) continue;

    const shortLabel =
      artefact.plan_name?.trim() ||
      artefactRowLabel(artefact, run.catalogue_key);
    incomplete.push(summarizeArtefactBlockers(shortLabel, itemReasons, artefacts.length > 1));
  }
  return incomplete;
}

/** One line per artefact — avoids repeating the full PDF title on every gap. */
function summarizeArtefactBlockers(
  label: string,
  itemReasons: string[],
  includeLabel: boolean,
): string {
  const gaps: string[] = [];
  for (const reason of itemReasons) {
    if (/checklist/i.test(reason)) gaps.push(reason.charAt(0).toLowerCase() + reason.slice(1));
    else if (/overall rating/i.test(reason)) gaps.push("choose a rating");
    else if (/approve/i.test(reason)) gaps.push("choose approve or not approve");
    else if (/comment/i.test(reason)) gaps.push(reason.charAt(0).toLowerCase() + reason.slice(1));
    else if (/note/i.test(reason)) gaps.push(reason.charAt(0).toLowerCase() + reason.slice(1));
    else gaps.push(reason.charAt(0).toLowerCase() + reason.slice(1));
  }

  const detail = gaps.join(", ");
  if (!includeLabel) return detail.charAt(0).toUpperCase() + detail.slice(1);
  return `${label}: ${detail}`;
}

export function wizardNextBlockedReasons(args: {
  step: WizardStep;
  intentConfirmed: boolean;
  submitting: boolean;
  criteria: PhaseCriteria | null;
  phaseRating: StepRating | null;
  run: AgentRun;
  artefactRatings: ArtefactRatings;
}): string[] {
  const { step, intentConfirmed, submitting, criteria, phaseRating, run, artefactRatings } = args;
  if (submitting) return ["Saving your review…"];

  if (step === "intent") {
    return intentConfirmed ? [] : ["Confirm the task intent before continuing"];
  }

  if (step === "artefacts") {
    return artefactsIncompleteReasons(run, artefactRatings);
  }

  if ((step === "orchestrate" || step === "output") && criteria && phaseRating) {
    return phaseRatingIncompleteReasons(criteria, phaseRating);
  }

  return ["Finish this step before continuing"];
}

function prefixReasons(prefix: string, reasons: string[]): string[] {
  return reasons.map((r) => `${prefix} — ${r.charAt(0).toLowerCase()}${r.slice(1)}`);
}

/** Mirrors ReviewWizard `canSaveReview` gates with plain-English reasons. */
export function saveReviewBlockedReasons(args: {
  submitting: boolean;
  ratings: PhaseRatings;
  criteria: NonNullable<AgentRun["review_criteria"]> | null;
  hasArtefacts: boolean;
  artefactsDone: boolean;
  hasSavedReview: boolean;
  hasPartialArtefacts: boolean;
  run: AgentRun;
  artefactRatings: ArtefactRatings;
}): string[] {
  const {
    submitting,
    ratings,
    criteria,
    hasArtefacts,
    artefactsDone,
    hasSavedReview,
    hasPartialArtefacts,
    run,
    artefactRatings,
  } = args;

  if (submitting) return ["Saving your review…"];

  const reasons: string[] = [];
  if (criteria) {
    if (ratings.orchestration.human_rag === null) {
      reasons.push(
        ...prefixReasons(
          "Orchestration",
          phaseRatingIncompleteReasons(criteria.orchestration, ratings.orchestration),
        ),
      );
    }
    if (ratings.output.human_rag === null) {
      reasons.push(
        ...prefixReasons("Outcome", phaseRatingIncompleteReasons(criteria.output, ratings.output)),
      );
    }
  } else if (ratings.orchestration.human_rag === null || ratings.output.human_rag === null) {
    reasons.push("Finish rating orchestration and outcome before submitting");
  }

  if (hasArtefacts && !artefactsDone && !(hasSavedReview && hasPartialArtefacts)) {
    reasons.push(...artefactsIncompleteReasons(run, artefactRatings));
  }

  return reasons;
}

export function exportResultsBlockedReason(args: {
  catalogueKey: string | null | undefined;
  reviewedCount: number;
  exporting: boolean;
}): string | null {
  if (args.exporting) return "Export already in progress";
  if (!args.catalogueKey) return "Select a task type before exporting";
  if (args.reviewedCount === 0) return "No reviewed cases to export yet";
  return null;
}

export function clearQueueBlockedReason(args: {
  catalogueKey: string | null | undefined;
  outstandingCount: number;
}): string | null {
  if (!args.catalogueKey) return "Select a task type before clearing";
  if (args.outstandingCount === 0) return "Nothing left to clear — the queue is empty";
  return null;
}

export function moveRunsBlockedReason(selectedCount: number): string | null {
  if (selectedCount === 0) return "Select at least one case to move";
  return null;
}
